// biome-ignore-all lint/performance/noBarrelFile: Package entrypoint intentionally exports stable adapter-core helpers.
//
// Provider runtime integrations must always go through adapter integration.
// There is no parallel "lightweight integration" path outside adapter-core.
//
// Use a thin adapter when the provider only needs straightforward transport
// wiring into the shared runtime shell. Escalate to a full adapter as soon as
// the provider has any of the following:
// - continuation-specific behavior
// - bootstrap or readiness preparation
// - gateway transport requirements
// - provider-native workspace semantics
// - non-trivial parser/codec logic
// - provider-specific failure/diagnostics mapping
import { openMeldDaemonProviderAccessEvidenceSchema } from "@openmeld/schemas";
import { createProviderOriginalError } from "./provider-original-error.js";
export { AcpProcessSession, } from "./acp-process-session.js";
export { createAcpToolActivityTracker, } from "./acp-tool-activity.js";
import { bridgeAgentControllerConversationRef, buildAdapterAgentControllerConversationRef, resolveBridgedAgentControllerConversationId, } from "./conversation-bridge.js";
export { createAgentControllerProcessDiscoveryDiagnosticAggregator, listAgentControllerDescendantProcessIds, listAgentControllerSupervisedProcessIds, resolveAgentControllerDescendantProcessIds, resolveAgentControllerProcessSignalId, resolveAgentControllerProcessSignalTarget, resolveAgentControllerSupervisedProcessIds, signalAgentControllerProcessTree, spawnAgentControllerProcess, terminateRemainingAgentControllerProcesses, } from "./agent-controller-process.js";
export { bridgeAgentControllerConversationRef, buildAdapterAgentControllerConversationRef, resolveBridgedAgentControllerConversationId, resolveBuiltinAdapterConversationBridgeSpec, } from "./conversation-bridge.js";
export { buildAcpDispatchSpaceActionMcpServers, buildDispatchSpaceActionMcpToolIds, OPENMELD_SPACE_ACTION_MCP_SERVER_ID, OPENMELD_SPACE_ACTION_MCP_TOOL_NAMES, resolveDispatchSpaceActionMcpServer, } from "./dispatch-space-action-mcp.js";
export { buildCliLaunchInvocation, buildExecutableLaunchContract, materializeCliLaunchCommand, resolveExecutableOnPath, resolveExecutablePathsOnPath, resolveInstalledCommandPath, resolveInstalledCommandPaths, resolveInstalledNodeScriptLaunchCandidates, resolveInstalledNodeScriptLaunchContract, resolveLaunchCandidatesAfterVerification, resolveSiblingCommandPath, verifyCliLaunchContract, verifyLaunchContractFiles, } from "./launch-contract.js";
export { normalizeCommandEnvironment, WINDOWS_RUNTIME_ENV_KEYS, } from "./process-environment.js";
export { createProviderOriginalError } from "./provider-original-error.js";
export { createUnavailableProviderUsageLimits, createUnsupportedProviderUsageLimits, } from "./provider-usage-limits.js";
export { buildProviderExitError, classifyCliDriverTimeoutFailure, } from "./runtime-failure.js";
export { classifyRuntimePermissionDenialMessage, looksLikeRuntimePermissionDenialMessage, RUNTIME_PERMISSION_DENIED_REASON_CODE, readRuntimePermissionDenialDetails, } from "./runtime-permission-denial.js";
export { ADAPTER_TOOL_ACTIVITY_NAMES, resolveAdapterToolActivityName, resolveAdapterToolWorkActivity, } from "./tool-activity.js";
export const AGENT_CONTROLLER_CONVERSATION_OCCUPANCY_CONTRACT_VERSION = 1;
export const DEFAULT_GRACEFUL_INTERRUPT_TIMEOUT_MS = 10_000;
const DEFAULT_INTERRUPT_REASON = "service.interrupt";
const DEFAULT_CANCEL_REASON = "service.cancel";
const TEST_DISABLE_DEFAULT_INVOKE = Symbol.for("openmeld.test.disableDefaultInvoke");
export const createSharedDispatchShell = (input) => {
    const bridgeSpec = input.bridgeSpec ?? null;
    return async (dispatchInput) => {
        const transportMode = dispatchInput.transportMode ?? "local-cli";
        const transportInvoke = input.invokeByMode?.[transportMode];
        if (input.invokeByMode && !transportInvoke) {
            return {
                errorCode: "adapter.unreachable",
                errorMessage: `adapter.unreachable: ${input.adapterId} transport '${transportMode}' is not configured`,
                errorType: "adapter",
                status: "failed",
            };
        }
        const defaultInvoke = isDefaultInvokeDisabledForTests(input.runtimeOptions)
            ? null
            : (input.resolveDefaultInvoke() ?? null);
        const resolvedInvoke = transportInvoke ?? input.invoke ?? defaultInvoke;
        if (!resolvedInvoke) {
            return {
                errorCode: "controller.runtime.missing",
                errorMessage: `controller.runtime.missing: ${input.adapterId} invoke runtime is unavailable`,
                errorType: "adapter",
                status: "failed",
            };
        }
        try {
            const invokeRequest = input.resolveInvokeRequest?.({
                bridgeSpec,
                request: dispatchInput,
            }) ??
                buildDefaultInvokeRequest({
                    bridgeSpec,
                    request: dispatchInput,
                });
            const requiredRepairConversationId = resolveRequiredRepairConversationId({
                invokeRequest,
                policy: input.repairConversationPolicy,
                request: dispatchInput,
            });
            const result = await resolvedInvoke(invokeRequest);
            const contextDelta = input.resolveSuccessContextDelta?.({
                bridgeSpec,
                invokeRequest,
                request: dispatchInput,
                result,
            }) ??
                resolveCanonicalContextDelta({
                    bridgeSpec,
                    contextDelta: result.contextDelta,
                });
            const protectedContextDelta = requireCanonicalRepairConversation({
                canonicalConversationId: requiredRepairConversationId,
                contextDelta,
            });
            return {
                ...(protectedContextDelta
                    ? { contextDelta: protectedContextDelta }
                    : {}),
                ...(result.diagnostics ? { diagnostics: result.diagnostics } : {}),
                output: result.output,
                status: "success",
                ...(result.telemetry ? { telemetry: result.telemetry } : {}),
            };
        }
        catch (error) {
            return toAdapterDispatchFailure(error, "adapter");
        }
    };
};
class AdapterRepairConversationMissingError extends Error {
    code = "repair.conversation_missing";
    errorType = "adapter";
    constructor() {
        super("repair.conversation_missing: repair requires an existing Agent Controller conversation");
        this.name = "AdapterRepairConversationMissingError";
    }
}
class AdapterRepairConversationError extends Error {
    code = "repair.conversation_changed";
    errorType = "adapter";
    constructor() {
        super("repair.conversation_changed: repair could not preserve the existing Agent Controller conversation");
        this.name = "AdapterRepairConversationError";
    }
}
const resolveRequiredRepairConversationId = (input) => {
    if (input.policy !== "require_canonical" ||
        input.request.executionMode !== "repair") {
        return null;
    }
    const canonicalConversationId = normalizeOptionalText(input.invokeRequest.agentControllerConversationId);
    if (!canonicalConversationId) {
        throw new AdapterRepairConversationMissingError();
    }
    return canonicalConversationId;
};
const requireCanonicalRepairConversation = (input) => {
    if (!input.canonicalConversationId) {
        return input.contextDelta;
    }
    const observedConversationId = normalizeOptionalText(input.contextDelta?.agentControllerConversationId);
    if (input.contextDelta?.refreshed === true ||
        observedConversationId !== input.canonicalConversationId) {
        throw new AdapterRepairConversationError();
    }
    return input.contextDelta;
};
export const createSharedControlShell = (input) => ({
    cancel: async (requestInput) => {
        const request = buildAdapterCancelRequest(requestInput);
        const sendCancelSignal = resolveCancelSignalSender({
            sendCancel: input.sendCancel,
            sendInterrupt: input.sendInterrupt,
        });
        if (!sendCancelSignal) {
            return buildUnsupportedCancelResult({
                taskId: request.taskId,
            });
        }
        try {
            await sendCancelSignal.send(request);
            return {
                accepted: true,
                deliveryPath: sendCancelSignal.deliveryPath,
                reasonCode: "accepted",
                signalDelivery: "sent",
                taskId: request.taskId,
            };
        }
        catch (error) {
            const reasonCode = resolveControlReasonCode(error) ?? "signal.send_failed";
            return buildUnsupportedCancelResult({
                reasonCode,
                reasonDetail: resolveControlReasonDetail(error, reasonCode),
                taskId: request.taskId,
            });
        }
    },
    interrupt: async (requestInput) => {
        const plan = buildInterruptPlan({
            gracefulTimeoutMs: requestInput.gracefulTimeoutMs ?? input.gracefulTimeoutMs,
            mode: requestInput.mode,
            reason: requestInput.reason,
            taskId: requestInput.taskId,
        });
        if (!input.sendInterrupt) {
            return buildUnsupportedInterruptResult({
                plan,
                reasonCode: "adapter.unsupported",
                taskId: requestInput.taskId,
            });
        }
        try {
            await executeInterruptPlan({
                plan,
                sendSignal: input.sendInterrupt,
                waitForMs: input.waitForMs,
            });
            return {
                accepted: true,
                plan,
                reasonCode: "accepted",
                signalDelivery: "sent",
                taskId: requestInput.taskId,
            };
        }
        catch (error) {
            const reasonCode = resolveControlReasonCode(error) ?? "signal.send_failed";
            return buildUnsupportedInterruptResult({
                plan,
                reasonCode,
                reasonDetail: resolveControlReasonDetail(error, reasonCode),
                taskId: requestInput.taskId,
            });
        }
    },
});
export const invokeWithSingleContextHeal = async (input) => {
    try {
        const first = await input.invokeOnce(input.contextId);
        return {
            contextHealed: false,
            contextId: first.contextId,
            ...(first.diagnostics ? { diagnostics: first.diagnostics } : {}),
            output: first.output,
            telemetry: first.telemetry,
        };
    }
    catch (firstError) {
        if (!(input.contextId && input.isContextLostError(firstError))) {
            throw firstError;
        }
        if (input.contextLossPolicy === "fail") {
            throw firstError;
        }
        await input.onContextLost?.();
        try {
            const healed = await input.invokeOnce(null);
            return {
                contextHealed: true,
                contextId: healed.contextId,
                ...(healed.diagnostics ? { diagnostics: healed.diagnostics } : {}),
                output: healed.output,
                telemetry: healed.telemetry
                    ? {
                        ...healed.telemetry,
                        contextHealed: true,
                        resumedContext: input.contextId !== null,
                    }
                    : undefined,
            };
        }
        catch (healError) {
            throw input.createRebuildFailedError(healError);
        }
    }
};
export const buildInterruptPlan = (input) => {
    const mode = input.mode ?? "graceful";
    const reason = input.reason ?? DEFAULT_INTERRUPT_REASON;
    if (mode === "force") {
        return {
            initial: {
                mode: "force",
                reason,
                taskId: input.taskId,
            },
        };
    }
    const delayMs = normalizeGracefulTimeoutMs(input.gracefulTimeoutMs);
    return {
        initial: {
            mode: "graceful",
            reason,
            taskId: input.taskId,
        },
        escalation: {
            delayMs,
            mode: "force",
            reason,
            taskId: input.taskId,
        },
    };
};
export const executeInterruptPlan = async (input) => {
    await input.sendSignal(input.plan.initial);
    if (!input.plan.escalation) {
        return;
    }
    const waitForMs = input.waitForMs ?? defaultWaitForMs;
    await waitForMs(input.plan.escalation.delayMs);
    await input.sendSignal({
        mode: input.plan.escalation.mode,
        reason: input.plan.escalation.reason,
        taskId: input.plan.escalation.taskId,
    });
};
export const buildUnsupportedInterruptResult = (input) => ({
    accepted: false,
    plan: input.plan,
    reasonCode: input.reasonCode ?? "adapter.unsupported",
    ...(input.reasonDetail ? { reasonDetail: input.reasonDetail } : {}),
    signalDelivery: "not_sent",
    taskId: input.taskId,
});
export const buildUnsupportedCancelResult = (input) => ({
    accepted: false,
    reasonCode: input.reasonCode ?? "adapter.unsupported",
    ...(input.reasonDetail ? { reasonDetail: input.reasonDetail } : {}),
    signalDelivery: "not_sent",
    taskId: input.taskId,
});
export const buildAdapterCancelRequest = (input) => ({
    reason: normalizeControlReason(input.reason, DEFAULT_CANCEL_REASON),
    taskId: input.taskId,
});
export const toAdapterDispatchFailure = (error, defaultErrorType = "adapter") => {
    const errorCode = extractErrorCode(error);
    const agentControllerConversationOccupancy = extractAgentControllerConversationOccupancy(error);
    const errorType = extractErrorType(error) ?? defaultErrorType;
    const diagnostics = extractDispatchDiagnostics(error);
    const providerAccessEvidence = extractProviderAccessEvidence(error);
    const originalError = extractProviderOriginalError(error);
    const telemetry = extractDispatchTelemetry(error);
    return {
        ...(agentControllerConversationOccupancy
            ? { agentControllerConversationOccupancy }
            : {}),
        ...(diagnostics ? { diagnostics } : {}),
        ...(errorCode ? { errorCode } : {}),
        errorMessage: error instanceof Error ? error.message : "adapter dispatch failed",
        errorType,
        ...(originalError ? { originalError } : {}),
        ...(providerAccessEvidence ? { providerAccessEvidence } : {}),
        status: "failed",
        ...(telemetry ? { telemetry } : {}),
    };
};
const extractAgentControllerConversationOccupancy = (error) => {
    if (!(error && typeof error === "object")) {
        return null;
    }
    const value = error
        .agentControllerConversationOccupancy;
    if (!(value && typeof value === "object")) {
        return null;
    }
    const occupancy = value;
    const agentControllerConversationId = typeof occupancy.agentControllerConversationId === "string"
        ? occupancy.agentControllerConversationId.trim()
        : "";
    if (!agentControllerConversationId ||
        occupancy.contractVersion !==
            AGENT_CONTROLLER_CONVERSATION_OCCUPANCY_CONTRACT_VERSION ||
        occupancy.state !== "occupied") {
        return null;
    }
    return {
        agentControllerConversationId,
        contractVersion: AGENT_CONTROLLER_CONVERSATION_OCCUPANCY_CONTRACT_VERSION,
        state: "occupied",
    };
};
const extractProviderOriginalError = (error) => {
    if (!(error && typeof error === "object")) {
        return null;
    }
    return createProviderOriginalError(error.originalError);
};
const extractDispatchTelemetry = (error) => {
    if (!error || typeof error !== "object") {
        return null;
    }
    const telemetry = toUnknownRecord(error.telemetry);
    if (!(telemetry &&
        isFiniteNumber(telemetry.completedAtMs) &&
        isFiniteNumber(telemetry.invokedAtMs) &&
        typeof telemetry.resumedContext === "boolean")) {
        return null;
    }
    const tokenUsage = extractAdapterTokenUsage(telemetry.tokenUsage);
    return {
        completedAtMs: telemetry.completedAtMs,
        ...(typeof telemetry.contextCompactionObserved === "boolean"
            ? { contextCompactionObserved: telemetry.contextCompactionObserved }
            : {}),
        ...(typeof telemetry.contextHealed === "boolean"
            ? { contextHealed: telemetry.contextHealed }
            : {}),
        ...(isFiniteNumber(telemetry.exitCode)
            ? { exitCode: telemetry.exitCode }
            : {}),
        ...(isFiniteNumber(telemetry.exitObservedAtMs)
            ? { exitObservedAtMs: telemetry.exitObservedAtMs }
            : {}),
        ...(isNodeSignal(telemetry.exitSignal)
            ? { exitSignal: telemetry.exitSignal }
            : {}),
        ...(isFiniteNumber(telemetry.firstOutputAtMs)
            ? { firstOutputAtMs: telemetry.firstOutputAtMs }
            : {}),
        ...(isFiniteNumber(telemetry.firstStderrAtMs)
            ? { firstStderrAtMs: telemetry.firstStderrAtMs }
            : {}),
        ...(isFiniteNumber(telemetry.firstStdoutAtMs)
            ? { firstStdoutAtMs: telemetry.firstStdoutAtMs }
            : {}),
        invokedAtMs: telemetry.invokedAtMs,
        ...(typeof telemetry.previewEmitted === "boolean"
            ? { previewEmitted: telemetry.previewEmitted }
            : {}),
        ...(isFiniteNumber(telemetry.processId)
            ? { processId: telemetry.processId }
            : {}),
        ...(isFiniteNumber(telemetry.processSpawnedAtMs)
            ? { processSpawnedAtMs: telemetry.processSpawnedAtMs }
            : {}),
        resumedContext: telemetry.resumedContext,
        ...(isFiniteNumber(telemetry.stdinClosedAtMs)
            ? { stdinClosedAtMs: telemetry.stdinClosedAtMs }
            : {}),
        ...(telemetry.streamingMode === "final" ||
            telemetry.streamingMode === "stream"
            ? { streamingMode: telemetry.streamingMode }
            : {}),
        ...(isFiniteNumber(telemetry.timeoutTriggeredAtMs)
            ? { timeoutTriggeredAtMs: telemetry.timeoutTriggeredAtMs }
            : {}),
        ...(tokenUsage ? { tokenUsage } : {}),
    };
};
function extractAdapterTokenUsage(value) {
    const usage = toUnknownRecord(value);
    const last = extractAdapterTokenUsageBreakdown(usage?.last);
    const total = extractAdapterTokenUsageBreakdown(usage?.total);
    const modelContextWindow = usage?.modelContextWindow;
    if (!(usage &&
        last &&
        total &&
        (modelContextWindow === null || isNonNegativeInteger(modelContextWindow)))) {
        return null;
    }
    return { last, modelContextWindow, total };
}
function extractAdapterTokenUsageBreakdown(value) {
    const usage = toUnknownRecord(value);
    if (!(usage &&
        isNonNegativeInteger(usage.cachedInputTokens) &&
        isNonNegativeInteger(usage.inputTokens) &&
        isNonNegativeInteger(usage.outputTokens) &&
        isNonNegativeInteger(usage.reasoningOutputTokens) &&
        isNonNegativeInteger(usage.totalTokens) &&
        (usage.cacheWriteInputTokens === undefined ||
            isNonNegativeInteger(usage.cacheWriteInputTokens)))) {
        return null;
    }
    return {
        ...(usage.cacheWriteInputTokens === undefined
            ? {}
            : { cacheWriteInputTokens: usage.cacheWriteInputTokens }),
        cachedInputTokens: usage.cachedInputTokens,
        inputTokens: usage.inputTokens,
        outputTokens: usage.outputTokens,
        reasoningOutputTokens: usage.reasoningOutputTokens,
        totalTokens: usage.totalTokens,
    };
}
function isNonNegativeInteger(value) {
    return Number.isInteger(value) && Number(value) >= 0;
}
function isFiniteNumber(value) {
    return typeof value === "number" && Number.isFinite(value);
}
function isNodeSignal(value) {
    return typeof value === "string" && value.startsWith("SIG");
}
const extractDispatchDiagnostics = (error) => {
    if (!error || typeof error !== "object") {
        return null;
    }
    const diagnostics = error.diagnostics;
    const record = toUnknownRecord(diagnostics);
    const modelNegotiation = parseModelNegotiationDiagnostics(record?.modelNegotiation);
    return modelNegotiation ? { modelNegotiation } : null;
};
const MODEL_DIAGNOSTIC_ID_LIMIT = 100;
const MODEL_DIAGNOSTIC_ID_MAX_LENGTH = 512;
function parseModelNegotiationDiagnostics(value) {
    const record = toUnknownRecord(value);
    if (!record) {
        return null;
    }
    const configAvailableModelIds = parseBoundedModelIds(record.configAvailableModelIds);
    const legacyAvailableModelIds = parseBoundedModelIds(record.legacyAvailableModelIds);
    const configAvailableModelCount = parseModelCount(record.configAvailableModelCount, configAvailableModelIds?.length);
    const legacyAvailableModelCount = parseModelCount(record.legacyAvailableModelCount, legacyAvailableModelIds?.length);
    const configCurrentModelId = parseNullableModelId(record.configCurrentModelId);
    const currentModelId = parseNullableModelId(record.currentModelId);
    const legacyCurrentModelId = parseNullableModelId(record.legacyCurrentModelId);
    const requestedModelId = parseNullableModelId(record.requestedModelId);
    if ((record.checkAttempt !== 1 && record.checkAttempt !== 2) ||
        configAvailableModelIds === null ||
        configAvailableModelCount === null ||
        typeof record.configAvailableModelIdsTruncated !== "boolean" ||
        configCurrentModelId === undefined ||
        currentModelId === undefined ||
        legacyAvailableModelIds === null ||
        legacyAvailableModelCount === null ||
        typeof record.legacyAvailableModelIdsTruncated !== "boolean" ||
        legacyCurrentModelId === undefined ||
        record.protocol !== "acp/v1" ||
        requestedModelId === undefined ||
        typeof record.requestedPresentInConfigOptions !== "boolean" ||
        typeof record.requestedPresentInLegacyModels !== "boolean" ||
        (record.sessionOperation !== "load" &&
            record.sessionOperation !== "new" &&
            record.sessionOperation !== "resume") ||
        !isModelSourceState(record.sourceState)) {
        return null;
    }
    return {
        checkAttempt: record.checkAttempt,
        configAvailableModelCount,
        configAvailableModelIds,
        configAvailableModelIdsTruncated: record.configAvailableModelIdsTruncated,
        configCurrentModelId,
        currentModelId,
        legacyAvailableModelCount,
        legacyAvailableModelIds,
        legacyAvailableModelIdsTruncated: record.legacyAvailableModelIdsTruncated,
        legacyCurrentModelId,
        protocol: record.protocol,
        requestedModelId,
        requestedPresentInConfigOptions: record.requestedPresentInConfigOptions,
        requestedPresentInLegacyModels: record.requestedPresentInLegacyModels,
        sessionOperation: record.sessionOperation,
        sourceState: record.sourceState,
    };
}
function parseBoundedModelIds(value) {
    if (!Array.isArray(value) ||
        value.length > MODEL_DIAGNOSTIC_ID_LIMIT ||
        !value.every((modelId) => typeof modelId === "string" &&
            modelId.length > 0 &&
            modelId.length <= MODEL_DIAGNOSTIC_ID_MAX_LENGTH)) {
        return null;
    }
    return [...value];
}
function parseModelCount(value, visibleCount) {
    return typeof value === "number" &&
        Number.isSafeInteger(value) &&
        value >= (visibleCount ?? Number.POSITIVE_INFINITY)
        ? value
        : null;
}
function parseNullableModelId(value) {
    if (value === null) {
        return null;
    }
    return typeof value === "string" &&
        value.length > 0 &&
        value.length <= MODEL_DIAGNOSTIC_ID_MAX_LENGTH
        ? value
        : undefined;
}
function isModelSourceState(value) {
    return (value === "config_options_only" ||
        value === "conflicting" ||
        value === "consistent" ||
        value === "legacy_models_only" ||
        value === "missing");
}
function toUnknownRecord(value) {
    return value && typeof value === "object" && !Array.isArray(value)
        ? value
        : null;
}
export const resolveAdapterAgentContext = (input) => {
    const env = input.env ?? {};
    const rawValue = env[input.spec.sourceField];
    const agentControllerConversationId = typeof rawValue === "string" && rawValue.trim().length > 0
        ? rawValue.trim()
        : null;
    const agentControllerConversation = input.bridgeSpec && agentControllerConversationId
        ? buildAdapterAgentControllerConversationRef({
            agentControllerRef: input.bridgeSpec.agentControllerRef,
            agentControllerConversationId,
            agentControllerConversationKind: input.bridgeSpec.agentControllerConversationKind,
        })
        : null;
    return {
        agentControllerConversationId,
        sourceField: input.spec.sourceField,
        mappingVersion: input.spec.mappingVersion,
        ...(agentControllerConversation ? { agentControllerConversation } : {}),
    };
};
const defaultWaitForMs = async (delayMs) => {
    if (delayMs <= 0) {
        return;
    }
    await new Promise((resolve) => {
        setTimeout(() => {
            resolve();
        }, delayMs);
    });
};
const normalizeGracefulTimeoutMs = (gracefulTimeoutMs) => {
    if (!Number.isInteger(gracefulTimeoutMs) || gracefulTimeoutMs === undefined) {
        return DEFAULT_GRACEFUL_INTERRUPT_TIMEOUT_MS;
    }
    return gracefulTimeoutMs > 0
        ? gracefulTimeoutMs
        : DEFAULT_GRACEFUL_INTERRUPT_TIMEOUT_MS;
};
const extractErrorCode = (error) => {
    if (!error || typeof error !== "object") {
        return null;
    }
    const code = error.code;
    if (typeof code !== "string") {
        return null;
    }
    const normalized = code.trim();
    return normalized.length > 0 ? normalized : null;
};
const extractErrorType = (error) => {
    if (!error || typeof error !== "object") {
        return null;
    }
    const errorType = error.errorType;
    if (typeof errorType !== "string") {
        return null;
    }
    const normalized = errorType.trim();
    switch (normalized) {
        case "adapter":
            return "adapter";
        case "auth":
            return "auth";
        case "config":
            return "config";
        case "timeout":
            return "timeout";
        case "network":
            return "network";
        case "unknown":
            return "unknown";
        default:
            return null;
    }
};
const extractProviderAccessEvidence = (error) => {
    if (!error || typeof error !== "object") {
        return null;
    }
    const evidence = error
        .providerAccessEvidence;
    if (!evidence || typeof evidence !== "object") {
        return null;
    }
    const parsed = openMeldDaemonProviderAccessEvidenceSchema.safeParse(evidence);
    return parsed.success ? parsed.data : null;
};
const normalizeControlReason = (reason, fallback) => {
    if (typeof reason !== "string") {
        return fallback;
    }
    const normalized = reason.trim();
    return normalized.length > 0 ? normalized : fallback;
};
const resolveCanonicalContextDelta = (input) => {
    if (!input.contextDelta) {
        return undefined;
    }
    const canonicalContextDelta = {
        agentControllerConversationId: input.contextDelta.agentControllerConversationId,
        ...(input.contextDelta.refreshed === true ? { refreshed: true } : {}),
        ...(input.contextDelta.agentControllerConversation
            ? {
                agentControllerConversation: input.contextDelta.agentControllerConversation,
            }
            : {}),
    };
    if (!input.bridgeSpec) {
        return canonicalContextDelta;
    }
    const agentControllerConversation = bridgeAgentControllerConversationRef({
        agentControllerConversationId: canonicalContextDelta.agentControllerConversationId,
        bridgeSpec: input.bridgeSpec,
        agentControllerConversation: canonicalContextDelta.agentControllerConversation,
    });
    return {
        ...canonicalContextDelta,
        ...(agentControllerConversation ? { agentControllerConversation } : {}),
    };
};
const buildDefaultInvokeRequest = (input) => {
    const agentControllerConversationId = input.bridgeSpec
        ? resolveBridgedAgentControllerConversationId({
            agentControllerConversationId: input.request.agentControllerConversationId,
            bridgeSpec: input.bridgeSpec,
            agentControllerConversation: input.request.agentControllerConversation,
        })
        : normalizeOptionalText(input.request.agentControllerConversationId);
    return {
        ...input.request,
        agentControllerConversationId,
    };
};
const normalizeOptionalText = (value) => {
    if (typeof value !== "string") {
        return null;
    }
    const normalized = value.trim();
    return normalized.length > 0 ? normalized : null;
};
const isDefaultInvokeDisabledForTests = (runtimeOptions) => runtimeOptions?.[TEST_DISABLE_DEFAULT_INVOKE] === true;
const resolveCancelSignalSender = (input) => {
    if (input.sendCancel) {
        return {
            deliveryPath: "cancel",
            send: input.sendCancel,
        };
    }
    if (!input.sendInterrupt) {
        return null;
    }
    return {
        deliveryPath: "force_interrupt_fallback",
        send: async (request) => {
            await input.sendInterrupt?.({
                mode: "force",
                reason: request.reason ?? DEFAULT_CANCEL_REASON,
                taskId: request.taskId,
            });
        },
    };
};
const resolveControlReasonCode = (error) => {
    const code = extractErrorCode(error);
    switch (code) {
        case "adapter.unsupported":
        case "signal.not_sent":
        case "signal.send_failed":
        case "task.not_found":
        case "task.not_running":
            return code;
        default:
            return null;
    }
};
const toUnknownErrorMessage = (error) => error instanceof Error ? error.message : "unknown adapter control error";
const resolveControlReasonDetail = (error, reasonCode) => reasonCode === "adapter.unsupported"
    ? undefined
    : toUnknownErrorMessage(error);
